import type { NonConformityRow } from '@obraplan/core/application/use-cases/quality/non-conformity-use-cases';

/**
 * O que a aba Não conformidades faz com a lista na tela (Fase 37, RN-481): a situação que se
 * olha, a busca e a ordem. A lista chega inteira do servidor; isto é só o recorte.
 */

export type NonConformityFilter = 'OPEN' | 'RESOLVED' | 'CANCELLED' | 'ALL';

export const NON_CONFORMITY_FILTERS: readonly {
  readonly id: NonConformityFilter;
  readonly label: string;
}[] = [
  { id: 'OPEN', label: 'Abertas' },
  { id: 'RESOLVED', label: 'Resolvidas' },
  { id: 'CANCELLED', label: 'Canceladas' },
  { id: 'ALL', label: 'Todas' },
];

export function countByFilter(
  rows: readonly NonConformityRow[],
): Readonly<Record<NonConformityFilter, number>> {
  const conta = { OPEN: 0, RESOLVED: 0, CANCELLED: 0, ALL: rows.length };
  for (const row of rows) conta[row.status] += 1;
  return conta;
}

/** Sem acento e sem caixa: "junta" acha "JUNTA", "não" acha "nao". */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase('pt-BR');
}

/**
 * A busca olha o número ("48", "nº 48") e todo texto que a linha mostra. O termo só de
 * algarismos é o número da NC, ou um número inteiro no texto ("4º pavimento", "25 mm"): "4" não
 * acha a NC 48.
 */
function casa(row: NonConformityRow, termos: readonly string[]): boolean {
  const texto = normalizar(
    [
      row.localLabel,
      row.towerName ?? '',
      row.serviceName,
      row.checklistName,
      row.itemLabel,
      row.description,
      row.solution ?? '',
      row.ownerName ?? '',
      row.openedByName,
      row.cancelReason ?? '',
      ...row.places.map((place) => place.label),
    ].join(' '),
  );
  return termos.every((termo) =>
    /^\d+$/.test(termo)
      ? row.number === Number(termo) || new RegExp(`(^|\\D)${termo}(\\D|$)`).test(texto)
      : texto.includes(termo),
  );
}

/**
 * As linhas da situação escolhida que casam com a busca, na ordem de trabalhar nelas.
 *
 * A aberta mais antiga primeiro: é a que está esperando há mais tempo. Nas outras situações,
 * a mais nova primeiro — quem olha as resolvidas quer o que acabou de acontecer.
 */
export function visibleRows(
  rows: readonly NonConformityRow[],
  filter: NonConformityFilter,
  query: string,
): NonConformityRow[] {
  const termos = normalizar(query)
    .replace(/n[º°o]\s*/g, '')
    .split(/\s+/)
    .filter((termo) => termo !== '');
  return rows
    .filter((row) => filter === 'ALL' || row.status === filter)
    .filter((row) => termos.length === 0 || casa(row, termos))
    .sort((a, b) =>
      filter === 'OPEN' ? a.date.localeCompare(b.date) || a.number - b.number : b.number - a.number,
    );
}

/** A quantidade na unidade do serviço: `6,5 m²`. */
export function formatNcQuantity(
  row: Pick<NonConformityRow, 'quantity' | 'unitName' | 'unitDecimals'>,
): string | undefined {
  if (row.quantity === undefined) return undefined;
  const numero = row.quantity.toLocaleString('pt-BR', {
    maximumFractionDigits: row.unitDecimals ?? 6,
  });
  return row.unitName === undefined ? numero : `${numero} ${row.unitName}`;
}

/** Hoje, no relógio de quem olha, como data de negócio (`YYYY-MM-DD`). */
export function todayBusinessDate(agora: Date = new Date()): string {
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');
  return `${String(agora.getFullYear())}-${mes}-${dia}`;
}

/** Quantos dias entre duas datas de negócio — sem `Date` local, que traria o fuso (RN-120). */
export function daysBetween(from: string, to: string): number {
  const [ay, am, ad] = from.split('-').map(Number);
  const [by, bm, bd] = to.split('-').map(Number);
  const a = Date.UTC(ay!, am! - 1, ad);
  const b = Date.UTC(by!, bm! - 1, bd);
  return Math.round((b - a) / 86_400_000);
}

/** "Aberta hoje", "Aberta ontem", "Aberta há 12 dias" — a situação da aberta, com a idade. */
export function openFor(date: string, today: string): string {
  const dias = Math.max(daysBetween(date, today), 0);
  if (dias === 0) return 'Aberta hoje';
  if (dias === 1) return 'Aberta ontem';
  return `Aberta há ${String(dias)} dias`;
}
